import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { PrincipalRiskLevel } from '@dsr/shared-types';
import { useOrganizations } from '../api/queries/clients';
import { usePrincipals, type PrincipalFilters } from '../api/queries/principals';
import { can } from '../auth/store';
import { Button, Select, type SelectOption } from '../components/ui';
import { DirectoryView } from '../components/directory/DirectoryView';
import type { DirectoryEntry } from '../components/directory/types';
import { initialsOf } from '../components/directory/initials';
import { RISK_LEVELS } from '../components/clients/meta';
import { CreateOrganizationModal } from '../components/clients/CreateOrganizationModal';
import { CreatePrincipalModal } from '../components/clients/CreatePrincipalModal';

// Directory › Individuals merges two sources into one people-directory list:
//   • HNI-type client organizations (individual clients), gated on org.read
//   • Protected principals, gated on principal.read
// A user with only one permission still sees that half (each query is disabled
// when its permission is absent, so no 403 noise). When the URL carries
// ?organization_id=N (the org-detail "View all principals" deep link) we narrow
// to that client's protectees and drop the HNI-client half.
export function DirectoryIndividualsPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const orgFromUrl = searchParams.get('organization_id');
  const orgFilterId =
    orgFromUrl && !Number.isNaN(Number(orgFromUrl)) ? Number(orgFromUrl) : undefined;
  const orgFilterActive = orgFilterId !== undefined;

  const canOrg = can('org.read');
  const canPrincipal = can('principal.read');
  const canWriteOrg = can('org.write');
  const canWritePrincipal = can('principal.write');

  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [risk, setRisk] = useState<PrincipalRiskLevel | 'all'>('all');
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newPrincipalOpen, setNewPrincipalOpen] = useState(false);

  const principalFilters: PrincipalFilters = {};
  if (orgFilterActive) principalFilters.organization_id = orgFilterId;
  if (risk !== 'all') principalFilters.risk_level = risk;
  if (q) principalFilters.q = q;

  const orgsQuery = useOrganizations(q ? { q } : {}, { enabled: canOrg });
  const principalsQuery = usePrincipals(principalFilters, { enabled: canPrincipal });

  const orgNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of orgsQuery.data?.organizations ?? []) map.set(o.id, o.name);
    return map;
  }, [orgsQuery.data]);

  const orgEntries: DirectoryEntry[] =
    canOrg && !orgFilterActive
      ? (orgsQuery.data?.organizations ?? [])
          .filter((o) => o.type === 'hni')
          .map((o): DirectoryEntry => ({
            id: `org-${o.id}`,
            kind: 'client-individual',
            name: o.name,
            subtitle: o.legal_name,
            to: `/organizations/${o.id}`,
            initials: initialsOf(o.name),
            badges: [
              { label: 'Client', tone: 'info' },
              { label: o.status, className: `badge-${o.status}` },
            ],
            meta: [],
          }))
      : [];

  const principalEntries: DirectoryEntry[] = canPrincipal
    ? (principalsQuery.data?.principals ?? []).map((p): DirectoryEntry => ({
        id: `prin-${p.id}`,
        kind: 'principal',
        name: p.full_name,
        subtitle: p.alias,
        to: `/principals/${p.id}`,
        personId: p.person_id,
        photoPath: p.photo_path,
        initials: initialsOf(p.full_name),
        badges: [
          { label: 'Principal', tone: 'accent' },
          { label: p.risk_level, className: `badge-risk-${p.risk_level}` },
        ],
        meta: [
          {
            label: 'Client',
            value: p.organization_id
              ? orgNameById.get(p.organization_id) ?? `#${p.organization_id}`
              : '—',
          },
        ],
      }))
    : [];

  const entries = [...orgEntries, ...principalEntries].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const isLoading =
    (canOrg && orgsQuery.isLoading) ||
    (canPrincipal && principalsQuery.isLoading);
  const isError =
    (canOrg && orgsQuery.isError) || (canPrincipal && principalsQuery.isError);

  const riskFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All risk levels' },
    ...RISK_LEVELS.map((r) => ({ value: r, label: r })),
  ];

  const filterClientName = orgFilterActive
    ? orgNameById.get(orgFilterId) ?? `#${orgFilterId}`
    : null;

  return (
    <>
      <DirectoryView
        title="Individuals"
        subtitle="High-net-worth clients and protected principals."
        actions={
          <>
            {canWriteOrg && (
              <Button variant="ghost" onClick={() => setNewClientOpen(true)}>
                New individual
              </Button>
            )}
            {canWritePrincipal && (
              <Button variant="primary" onClick={() => setNewPrincipalOpen(true)}>
                New principal
              </Button>
            )}
          </>
        }
        filters={
          <>
            <form
              className="filter-search"
              onSubmit={(e) => {
                e.preventDefault();
                setQ(qDraft);
              }}
            >
              <input
                type="search"
                className="form-input"
                placeholder="Search by name or alias…"
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
              />
            </form>
            {canPrincipal && (
              <Select
                value={risk}
                onValueChange={(v) => setRisk(v as PrincipalRiskLevel | 'all')}
                options={riskFilterOptions}
                aria-label="Filter by risk level"
              />
            )}
            {filterClientName && (
              <Link to="/directory/individuals" className="dir-filter-chip">
                Client: {filterClientName} ✕
              </Link>
            )}
          </>
        }
        entries={entries}
        isLoading={isLoading}
        isError={isError}
        errorText="Failed to load individuals."
        emptyText="No individuals match the current filters."
      />
      {newClientOpen && (
        <CreateOrganizationModal
          defaultType="hni"
          title="New individual client"
          onClose={() => setNewClientOpen(false)}
        />
      )}
      {newPrincipalOpen && (
        <CreatePrincipalModal
          defaultOrgId={orgFilterActive ? orgFilterId : undefined}
          onClose={() => setNewPrincipalOpen(false)}
        />
      )}
    </>
  );
}
